import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Grid, Heading, Link } from '@printdeal/ui-library';
import { ErrorManagement } from '@printdeal/error-management/react';
import ArtworkThumbnail from '../../ArtworkThumbnail';
import { ServiceFactory } from '../../../../../services/ServiceFactory';
import { Complaint, Attachment } from '../../../../../types/complaints';
import { getByteSize } from '../../../../../helpers/byte-operations';

interface Props {
  complaint: Complaint;
  attachment: Attachment;
}

const ComplaintDetailAttachment = ({ complaint, attachment }: Props) => {
  const queryClient = useQueryClient();
  const isImage = (contentType: string) => contentType.toLowerCase().match(/gif|jpg|jpeg|png|tiff/);

  const getMimeType = (contentType: string) => contentType.split('/')[1].toLowerCase();

  const deleteAttachment = async () => {
    try {
      const complaintService = ServiceFactory.createComplaintsService();
      await complaintService.deleteAttachment(complaint.complaintId, attachment.id);
      queryClient.refetchQueries({
        queryKey: ['complaint', complaint.complaintId],
      });
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
      });
    }
  };

  return (
    <Grid className="pd-grid-cols-[1fr,4fr] pd-gap-2" data-cy="complaintAttachment">
      <Grid.Item>
        <ArtworkThumbnail uploadsUrl={isImage(attachment.contentType) ? attachment.href : undefined} />
      </Grid.Item>
      <Grid.Item>
        <Grid className="pd-grid-cols-[3fr,2fr] pd-gap-2">
          <Grid.Item>
            <Heading as="h3">{attachment?.description || '-'}</Heading>
            {attachment.size && attachment.contentType && (
              <span>{`${getByteSize(parseInt(attachment.size, 10))} - ${getMimeType(attachment.contentType)}`}</span>
            )}
          </Grid.Item>
          <Grid.Item className="pd-text-right">
            <Link
              href={attachment.href}
              rel="noopener noreferrer"
              target="_blank"
              variant="secondary"
              data-cy="downloadFile"
              iconBefore="download"
            >
              <FormattedMessage id="complaints.details.download.attachment" defaultMessage="Download attachment" />
            </Link>
            {complaint.status !== 'resolved' && (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <Link
                as="button"
                variant="secondary"
                iconBefore="trashcan"
                data-cy="deleteFile"
                onClick={deleteAttachment}
                className="pd-mt-2 hover:pd-cursor-pointer"
              >
                <FormattedMessage id="complaints.details.delete.attachment" defaultMessage="Delete attachment" />
              </Link>
            )}
          </Grid.Item>
        </Grid>
      </Grid.Item>
    </Grid>
  );
};

export default ComplaintDetailAttachment;
